"use client";

import { useEffect, useMemo, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { useAuth } from "@clerk/clerk-react";
import { useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { useMutationWithAuthRedirect } from "@/lib/convexAuth";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { VoiceGuideButton } from "@/components/voice/VoiceGuideButton";
import { usePageTitle } from "@/lib/usePageTitle";

function sanitizeNextParam(next: string | null) {
  if (!next) return "/dashboard";
  // Prevent open redirects.
  if (!next.startsWith("/")) return "/dashboard";
  return next;
}

function suggestUsername(email: string) {
  const base = email.split("@")[0] ?? "";
  const cleaned = base
    .trim()
    .toLowerCase()
    .replace(/^@/, "")
    .replace(/[^a-z0-9_-]+/g, "_")
    .replace(/^_+|_+$/g, "");
  return cleaned.slice(0, 20);
}

export default function OnboardingPage() {
  usePageTitle("Onboarding");
  const router = useRouter();
  const searchParams = useSearchParams();
  const next = useMemo(
    () => sanitizeNextParam(searchParams.get("next")),
    [searchParams]
  );

  const { isLoaded, isSignedIn } = useAuth();
  const user = useQuery(api.users.getCurrentUser);
  const ensureCurrentUser = useMutationWithAuthRedirect(api.users.ensureCurrentUser);
  const setUsername = useMutationWithAuthRedirect(api.users.setUsername);

  const [username, setUsernameInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;
    if (user !== null) return;
    void ensureCurrentUser({});
  }, [ensureCurrentUser, isLoaded, isSignedIn, user]);

  useEffect(() => {
    if (!user) return;
    if (user.username) {
      router.replace(next);
      return;
    }

    if (!username && user.email) {
      setUsernameInput(suggestUsername(user.email));
    }
  }, [next, router, user, username]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!username.trim()) {
      setError("Username is required");
      return;
    }

    setSaving(true);
    try {
      await setUsername({ username });
      router.replace(next);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to set username");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Navbar />
      <main className="flex flex-1 items-center justify-center px-4 py-12">
        <Card className="w-full max-w-lg">
          <CardHeader>
            <div className="flex items-start justify-between gap-3">
              <div>
                <CardTitle>Choose a Username</CardTitle>
                <CardDescription>
                  This is the name other players will see in chat. It must be unique.
                </CardDescription>
              </div>
              <VoiceGuideButton
                track="onboarding"
                label="Listen: quick intro"
                aria-label="Play onboarding voice guide"
                className="shrink-0"
              />
            </div>
          </CardHeader>
          <CardContent>
            {!isLoaded ? (
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            ) : !isSignedIn ? (
              <div className="space-y-4">
                <p className="text-muted">Please sign in to continue.</p>
                <Button onClick={() => router.push("/login")}>Sign In</Button>
              </div>
            ) : user === undefined ? (
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <label htmlFor="username" className="text-sm font-medium">
                    Username
                  </label>
                  <Input
                    id="username"
                    value={username}
                    onChange={(e) => setUsernameInput(e.target.value)}
                    placeholder="your_username"
                    autoComplete="username"
                  />
                  <p className="text-xs text-muted">
                    3-20 chars. Letters/numbers, underscores, and hyphens only.
                  </p>
                </div>

                {error && (
                  <p className="text-sm text-destructive">{error}</p>
                )}

                <Button type="submit" disabled={saving}>
                  {saving ? "Saving..." : "Continue"}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </main>
      <Footer />
    </div>
  );
}
